// ==================== RECENT CAMPAIGNS COMPONENT ====================

import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CheckCircle2, 
  Clock, 
  Zap, 
  XCircle, 
  FileText,
  ArrowRight,
} from 'lucide-react';
import { Card } from '../../../components/ui/Card';
import { Badge } from '../../../components/ui/Badge';
import { cn } from '../../../utils/helpers';
import { staggerItem } from '../../../utils/animations';
import type { RecentCampaignItem } from '../../../services/customer-dashboard.service';

// ==================== TYPES ====================

interface RecentCampaignsProps {
  campaigns: RecentCampaignItem[];
  isLoading?: boolean;
}

// ==================== STATUS CONFIG ====================

const STATUS_CONFIG: Record<string, { 
  label: string; 
  variant: 'default' | 'orange' | 'success' | 'error' | 'warning' | 'info';
  icon: typeof CheckCircle2;
}> = {
  RUNNING: { label: 'Running', variant: 'orange', icon: Zap },
  SCHEDULED: { label: 'Scheduled', variant: 'info', icon: Clock },
  COMPLETED: { label: 'Completed', variant: 'success', icon: CheckCircle2 },
  PAUSED: { label: 'Paused', variant: 'warning', icon: Clock },
  FAILED: { label: 'Failed', variant: 'error', icon: XCircle },
  DRAFT: { label: 'Draft', variant: 'default', icon: FileText },
  CANCELLED: { label: 'Cancelled', variant: 'default', icon: XCircle },
};

// ==================== COMPONENT ====================

export const RecentCampaigns = ({ campaigns, isLoading = false }: RecentCampaignsProps) => {
  if (isLoading) {
    return (
      <motion.div variants={staggerItem}>
        <Card padding="none">
          <div className="p-4 border-b border-(--color-border-gray)">
            <div className="h-5 w-36 bg-(--color-border-gray) rounded animate-pulse" />
          </div>
          <div className="p-3 space-y-2">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-12 bg-(--color-border-gray) rounded animate-pulse" />
            ))}
          </div>
        </Card>
      </motion.div>
    );
  }

  const getStatusBadge = (status: string) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.DRAFT;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const calculateReadRate = (campaign: RecentCampaignItem): string => {
    if (campaign.sentCount === 0) return '-';
    const rate = Math.round((campaign.readCount / campaign.sentCount) * 100);
    return `${rate}%`;
  };

  return (
    <motion.div variants={staggerItem}>
      <Card padding="none">
        <div className="px-4 py-3 border-b border-(--color-border-gray)">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-semibold text-white font-heading">Recent Campaigns</h3>
            <Link 
              to="/app/campaigns" 
              className="flex items-center gap-1 text-xs text-(--color-signal-orange) hover:underline"
            >
              View All
              <ArrowRight size={12} />
            </Link>
          </div>
        </div>
        
        {campaigns.length === 0 ? (
          <div className="p-6 text-center">
            <FileText size={32} className="text-(--color-muted-gray) mx-auto mb-2" />
            <p className="text-sm text-(--color-muted-gray)">No campaigns yet</p>
            <Link to="/app/campaigns" className="text-xs text-(--color-signal-orange) hover:underline mt-1 inline-block">
              Create your first campaign
            </Link>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-(--color-border-gray)">
                  <th className="px-4 py-2 text-left text-xs font-medium text-(--color-muted-gray) uppercase tracking-wider">
                    Campaign
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-(--color-muted-gray) uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-(--color-muted-gray) uppercase tracking-wider">
                    Sent
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-(--color-muted-gray) uppercase tracking-wider">
                    Read Rate
                  </th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((campaign) => (
                  <tr
                    key={campaign.id}
                    className="border-b border-(--color-border-gray) last:border-b-0 hover:bg-white/5 transition-colors"
                  >
                    <td className="px-4 py-2.5">
                      <Link 
                        to={`/app/campaigns/${campaign.id}`}
                        className="text-sm font-medium text-white hover:text-(--color-signal-orange) transition-colors"
                      >
                        {campaign.name}
                      </Link>
                    </td>
                    <td className="px-4 py-2.5">{getStatusBadge(campaign.status)}</td>
                    <td className="px-4 py-2.5">
                      <span className="text-sm text-(--color-soft-gray)">
                        {campaign.sentCount.toLocaleString()}
                        {campaign.recipientCount > 0 && (
                          <span className="text-(--color-muted-gray)">
                            /{campaign.recipientCount.toLocaleString()}
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={cn(
                        'text-sm',
                        campaign.sentCount > 0 && campaign.readCount / campaign.sentCount >= 0.5 
                          ? 'text-(--color-success)' 
                          : 'text-(--color-soft-gray)'
                      )}>
                        {calculateReadRate(campaign)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </motion.div>
  );
};

export default RecentCampaigns;
